import { useEffect, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import CommandCenterLayout from '@/frontend/layouts/CommandCenterLayout'
import IntegrationFilters from './components/IntegrationFilters'
import IntegrationsHeader from './components/IntegrationsHeader'
import IntegrationStatsRow from './components/IntegrationStatsRow'
import IntegrationTable from './components/IntegrationTable'
import type { AuthConfigCopy, ComposioIntegrationRow, IntegrationStats, Pagination } from './types'

interface Props {
  integrations: ComposioIntegrationRow[]
  stats: IntegrationStats
  pagination: Pagination
  search: string
  status: string
  authConfigsLoaded: boolean
  authConfigCopy: AuthConfigCopy
  accessManagement: { path: string; label: string }
}
function Index({ integrations, stats, pagination, search, status, authConfigsLoaded, authConfigCopy, accessManagement }: Props) {
  const [query, setQuery] = useState(search)
  const [selectedStatus, setSelectedStatus] = useState(status)
  const [busySlug, setBusySlug] = useState<string | null>(null)
  const [cacheBusy, setCacheBusy] = useState(false)
  const firstRender = useRef(true)
  const filterTimeout = useRef<number | null>(null)

  const currentFilters = { search: query, status: selectedStatus }
  const mutationBusy = busySlug !== null || cacheBusy

  const clearPendingFilterVisit = () => {
    if (filterTimeout.current === null) return

    window.clearTimeout(filterTimeout.current)
    filterTimeout.current = null
  }

  useEffect(() => {
    if (mutationBusy) {
      clearPendingFilterVisit()
    }
  }, [mutationBusy])

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }

    if (mutationBusy) return

    clearPendingFilterVisit()
    filterTimeout.current = window.setTimeout(() => {
      router.get('/cc/integrations', { search: query, status: selectedStatus }, {
        preserveState: true,
        preserveScroll: true,
        replace: true,
      })
    }, 300)

    return clearPendingFilterVisit
  }, [query, selectedStatus])

  const goToPage = (page: number) => {
    if (mutationBusy) return

    router.get('/cc/integrations', { ...currentFilters, page }, { preserveState: true })
  }

  const toggleIntegration = (integration: ComposioIntegrationRow) => {
    if (mutationBusy) return

    clearPendingFilterVisit()
    setBusySlug(integration.slug)
    router.patch(`/cc/integrations/${integration.slug}`, {
      stationzero_enabled: !integration.stationzeroEnabled,
      search: query,
      status: selectedStatus,
      page: pagination.page,
    }, {
      preserveScroll: true,
      onFinish: () => setBusySlug(null),
    })
  }

  const updateAuthConfig = (integration: ComposioIntegrationRow, authConfigId: string) => {
    if (mutationBusy || authConfigId === (integration.selectedAuthConfigId ?? '')) return

    clearPendingFilterVisit()
    setBusySlug(integration.slug)
    router.patch(`/cc/integrations/${integration.slug}`, {
      auth_config_id: authConfigId,
      search: query,
      status: selectedStatus,
      page: pagination.page,
    }, {
      preserveScroll: true,
      onFinish: () => setBusySlug(null),
    })
  }

  const resolveCache = () => {
    if (mutationBusy) return

    clearPendingFilterVisit()
    setCacheBusy(true)
    router.post('/cc/integrations/resolve-cache', {
      ...currentFilters,
      page: pagination.page,
    }, {
      preserveScroll: true,
      onFinish: () => setCacheBusy(false),
    })
  }

  return (
    <div className="space-y-6">
      <IntegrationsHeader busy={mutationBusy} cacheBusy={cacheBusy} onResolveCache={resolveCache} accessManagement={accessManagement} />

      <IntegrationStatsRow stats={stats} />

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <IntegrationFilters
          query={query}
          status={selectedStatus}
          onQueryChange={setQuery}
          onStatusChange={setSelectedStatus}
          disabled={mutationBusy}
        />
        <span className="text-sm text-muted-foreground">{pagination.total} integrações</span>
      </div>

      <IntegrationTable
        integrations={integrations}
        pagination={pagination}
        busySlug={busySlug}
        disabled={mutationBusy}
        authConfigsLoaded={authConfigsLoaded}
        authConfigCopy={authConfigCopy}
        onToggle={toggleIntegration}
        onAuthConfigChange={updateAuthConfig}
        onPageChange={goToPage}
      />
    </div>
  )
}

Index.layout = (page: React.ReactElement) => <CommandCenterLayout>{page}</CommandCenterLayout>

export default Index
